/**
 * Maturity Score Hero Component
 *
 * World-class pharmaceutical analytics hero card displaying:
 * - Overall maturity score with circular gauge (0-100)
 * - FAIR equivalent (0-5)
 * - Classification band with color coding
 * - Confidence percentage
 * - Delta from first assessment
 * - Last updated timestamp
 *
 * Design: Executive-ready, trustworthy aesthetics following pharma standards
 * Spec: Documentation/Development/Design Docs/Maturity_Dashboard_UI_UX_Specification.md
 */

import React from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import type { MaturityLevel } from '../../server/scoring/maturityNormalizer';

// ============================================================================
// TYPES
// ============================================================================

interface MaturityScoreHeroProps {
  score: number;              // 0-100
  fairEquivalent: number;     // 0-5
  classification: MaturityLevel;
  delta?: number;             // Change from first assessment
  stationsCompleted: number;
  stationsTotal: number;
  timestamp: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get score color based on maturity band
 */
const getScoreColor = (score: number): string => {
  if (score >= 90) return '#27AE60';      // Excellent
  if (score >= 75) return '#3498DB';      // Good
  if (score >= 60) return '#F39C12';      // Acceptable
  if (score >= 40) return '#E67E22';      // Fair
  if (score >= 20) return '#E74C3C';      // Poor
  return '#C0392B';                       // Critical
};

/**
 * Get classification label
 */
const getClassificationLabel = (classification: MaturityLevel): string => {
  const labels: Record<MaturityLevel, string> = {
    excellent: 'EXCELLENT',
    good: 'GOOD',
    acceptable: 'ACCEPTABLE',
    fair: 'FAIR',
    poor: 'POOR',
    critical: 'CRITICAL'
  };
  return labels[classification];
};

/**
 * Format timestamp for display
 */
const formatTimestamp = (timestamp: string): string => {
  try {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return timestamp;
  }
};

/**
 * Calculate confidence score (placeholder - should come from backend)
 * For now, we estimate based on stations completed
 */
const calculateConfidence = (stationsCompleted: number, stationsTotal: number): number => {
  const baseConfidence = 70;
  const bonusPerStation = 30 / stationsTotal;
  return Math.round(baseConfidence + (stationsCompleted * bonusPerStation));
};

// ============================================================================
// COMPONENT
// ============================================================================

export const MaturityScoreHero: React.FC<MaturityScoreHeroProps> = ({
  score,
  fairEquivalent,
  classification,
  delta,
  stationsCompleted,
  stationsTotal,
  timestamp
}) => {
  const scoreColor = getScoreColor(score);
  const classificationLabel = getClassificationLabel(classification);
  const formattedDate = formatTimestamp(timestamp);
  const confidence = calculateConfidence(stationsCompleted, stationsTotal);

  // Determine trend direction
  const trendDirection: 'up' | 'down' | 'stable' =
    delta === undefined || delta === 0 ? 'stable' :
    delta > 0 ? 'up' : 'down';

  const deltaValue = delta ?? 0;

  return (
    <div className="maturity-hero-card">
      {/* Circular Gauge */}
      <div className="score-visual">
        <CircularProgressbar
          value={score}
          text={`${Math.round(score)}`}
          strokeWidth={10}
          styles={buildStyles({
            pathColor: scoreColor,
            textColor: '#ffffff',
            trailColor: '#3d4152',
            textSize: '28px',
            pathTransitionDuration: 0.8,
          })}
        />
      </div>

      {/* Score Details */}
      <div className="score-details">
        {/* Primary Score Display */}
        <div className="score-primary">
          <span className="score-value">{score.toFixed(0)}</span>
          <span className="score-max">/100</span>
        </div>

        {/* Classification Badge */}
        <div className="classification-badge" style={{ backgroundColor: scoreColor }}>
          {classificationLabel}
        </div>

        {/* Metadata Grid */}
        <div className="score-metadata">
          <div className="metadata-row">
            <span className="label">FAIR Equivalent:</span>
            <span className="value">{fairEquivalent.toFixed(1)}/5.0</span>
          </div>
          <div className="metadata-row">
            <span className="label">Confidence:</span>
            <span className="value">{confidence}%</span>
          </div>
          <div className="metadata-row">
            <span className="label">Stations Completed:</span>
            <span className="value">{stationsCompleted} / {stationsTotal}</span>
          </div>
        </div>

        {/* Delta from First Assessment */}
        <div className="score-delta">
          {trendDirection === 'up' && <span style={{ color: '#27AE60' }}>▲</span>}
          {trendDirection === 'down' && <span style={{ color: '#E74C3C' }}>▼</span>}
          {trendDirection === 'stable' && <span style={{ color: '#9da5b8' }}>━</span>}
          <span>
            {deltaValue >= 0 ? '+' : ''}{deltaValue.toFixed(0)} pts from first assessment
          </span>
        </div>

        {/* Last Updated */}
        <div className="last-updated">
          Last Updated: {formattedDate}
        </div>
      </div>
    </div>
  );
};
